import locale from "@bonliva-crm/locale";
import {
  Box,
  InputLabel,
  makeStyles,
  Typography,
  FormControl,
  Chip
} from "@material-ui/core";
import ReactSelect, { GroupBase, OptionsOrGroups, Props } from "react-select5";
import { useSelectStyle } from "./hooks/use-select-style";
import { CheckboxOptionCollapsibleComponent } from "./components/checkbox-option-collapsible";
import { DropdownIndicatorComponent } from "./components/dropdown-indicator";
import { MagnifyingGlassIcon } from "@bonliva-crm/ui";
import { PlaceholderComponent } from "./components/placeholder";
import { SelectBadge } from "./components/select-badge";
import { useEffect, useState } from "react";
import { ClearIndicatorComponent } from "./components/clear-indicator";
import { MenuComponent } from "./components/menu";

export const filterOptionDefault = (
  option: any & { label: string },
  inputValue?: string
): boolean => {
  if (!inputValue) return true;
  return option.label?.toLowerCase().includes(inputValue.toLowerCase());
};

export function MultiCollapsible<
  Option,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  mainProps: Props<Option, true, Group> & {
    selectRef?: any;
    onCollapse: (option: Option) => void;
    isCollapsible: (option: Option) => boolean;
    isCollapsed: (option: Option) => boolean;
    isIndeterminate?: (option: Option) => boolean;
    isOptionStandAlone?: (option: Option) => boolean;
    setCollapsedGroups?: (groups: string[]) => void;
    isClearable?: boolean;
    onClear?: () => void;
    inputLabel?: string;
    showBadge?: boolean;
    styleProps?: Parameters<typeof useSelectStyle<Option, true, Group>>[0];
    sortOptionsOnMenuOpenFn?: (
      options: OptionsOrGroups<Option, Group>
    ) => OptionsOrGroups<Option, Group>;
    menuChildren?: {
      appendedChildren?: React.ReactNode;
      prependedChildren?: React.ReactNode;
    };
    isShowCollapsibleOptionBadge?: (option: Option) => boolean;
  }
) {
  const { selectStyle, selectTheme } = useSelectStyle<Option, true, Group>(
    mainProps.styleProps
  );
  const classes = useStyles();

  const { inputLabel, showBadge, isClearable, onClear, menuChildren, name } = mainProps;

  const [_selectedAmount, _setSelectedAmount] = useState(0);
  const selectedAmount =
    mainProps.value && Array.isArray(mainProps.value)
      ? mainProps.value.length
      : _selectedAmount;

  const firstValue =
    Array.isArray(mainProps.value) &&
    mainProps.value[0] &&
    "label" in mainProps.value[0] &&
    mainProps.value[0].label;

  const hasValue =
    mainProps.value && Array.isArray(mainProps.value) && mainProps.value.length > 0;
  const [isOpen, _setIsOpen] = useState(mainProps.menuIsOpen ?? false);
  const [options, setOptions] = useState<OptionsOrGroups<Option, Group>>(
    mainProps.options ?? []
  );

  useEffect(() => {
    setOptions(mainProps.options ?? []);
  }, [mainProps.options]);

  return (
    <Box className={classes.root}>
      {showBadge && (
        <SelectBadge
          selectedAmount={selectedAmount}
          text={firstValue ? (firstValue as string) : undefined}
        />
      )}
      <FormControl>
        {inputLabel && (
          <InputLabel htmlFor={name} shrink={!!hasValue || isOpen}>
            <Typography
              variant="body1"
              style={{ opacity: mainProps.isDisabled ? 0.5 : 1 }}
            >
              {inputLabel}
            </Typography>
          </InputLabel>
        )}

        <ReactSelect<Option, true, Group>
          placeholder={
            <Box className={classes.placeholder}>
              <MagnifyingGlassIcon />
              {locale.Search}...
            </Box>
          }
          components={{
            Option: (p) =>
              CheckboxOptionCollapsibleComponent({
                ...p,
                isCollapsible: mainProps.isCollapsible,
                isCollapsed: mainProps.isCollapsed,
                onCollapse: mainProps.onCollapse,
                isOptionStandAlone: mainProps.isOptionStandAlone,
                isShowCollapsibleOptionBadge: mainProps.isShowCollapsibleOptionBadge,
                isIndeterminate: mainProps.isIndeterminate
              }),
            Placeholder: (p) => PlaceholderComponent(p),
            DropdownIndicator: (p) => DropdownIndicatorComponent(p),
            ClearIndicator: (p) =>
              isClearable && onClear ? (
                ClearIndicatorComponent({ ...p, onClear: onClear })
              ) : (
                <></>
              ),
            IndicatorSeparator: null,
            MultiValue: (p) => {
              return (
                <Chip
                  size="small"
                  label={(p.data as any).label}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onDelete={() => {
                    mainProps.onChange?.(
                      (mainProps.value as Option[]).filter(
                        (v) => (v as any).value !== (p.data as any).value
                      ),
                      { action: "remove-value", removedValue: p.data }
                    );
                    showBadge &&
                      _setSelectedAmount((mainProps.value as Option[]).length - 1);
                  }}
                />
              );
            },
            Menu: MenuComponent
          }}
          styles={selectStyle}
          theme={(theme) => ({
            ...theme,
            ...selectTheme
          })}
          closeMenuOnSelect={true}
          hideSelectedOptions={true}
          isClearable={false}
          backspaceRemovesValue={true}
          noOptionsMessage={() => locale.NoMoreSearchResults}
          loadingMessage={() => ""}
          isMulti={true}
          isSearchable={true}
          onMenuClose={() => {
            _setIsOpen(false);
          }}
          onMenuOpen={() => {
            _setIsOpen(true);
            if (mainProps.sortOptionsOnMenuOpenFn) {
              options && setOptions(mainProps.sortOptionsOnMenuOpenFn(options.slice()));
            }
          }}
          menuChildren={menuChildren}
          ref={mainProps.selectRef}
          {...mainProps}
          options={options}
        />
      </FormControl>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "0.5rem",
    position: "relative"
  },
  placeholder: {
    display: "flex",
    columnGap: "0.5rem",
    alignItems: "center"
  }
}));
